/**
 * Frontend mirror of backend {@code ReferenceType}: wire type, KSUID prefix, and RAG lane.
 * Keep in lockstep with {@code com.swiftracks.cns.agent.reference.ReferenceType}.
 */
export const MENTION_REFERENCE_TYPES = [
  { wireType: "user", idPrefix: "usr_", mode: "argument" },
  { wireType: "document", idPrefix: "doc_", mode: "context" },
  { wireType: "experiment", idPrefix: "exp_", mode: "context" },
  { wireType: "hypothesis", idPrefix: "hyp_", mode: "context" },
  { wireType: "assumption", idPrefix: "asm_", mode: "context" },
  { wireType: "learning", idPrefix: "lrn_", mode: "context" },
  { wireType: "insight", idPrefix: "ins_", mode: "context" },
] as const;

/** Wire {@code type} on mention parts — mirrors backend {@code ReferenceType.wireType()}. */
export type MentionWireType = (typeof MENTION_REFERENCE_TYPES)[number]["wireType"];

const BY_WIRE_TYPE: Record<
  MentionWireType,
  (typeof MENTION_REFERENCE_TYPES)[number]
> = Object.fromEntries(
  MENTION_REFERENCE_TYPES.map((entry) => [entry.wireType, entry]),
) as Record<MentionWireType, (typeof MENTION_REFERENCE_TYPES)[number]>;

/** Closed set of wire types accepted as @-mentions (composer + messageParts). */
export const MENTION_WIRE_TYPES: readonly MentionWireType[] =
  MENTION_REFERENCE_TYPES.map((entry) => entry.wireType);

export function isMentionWireType(value: string): value is MentionWireType {
  return Object.prototype.hasOwnProperty.call(BY_WIRE_TYPE, value);
}

/** KSUID prefix for a mention wire type (e.g. {@code assumption} → {@code asm_}). */
export function mentionIdPrefix(type: MentionWireType): string {
  return BY_WIRE_TYPE[type].idPrefix;
}

/** Whether {@code publicId} carries the KSUID prefix for {@code type}. */
export function matchesMentionIdPrefix(
  type: MentionWireType,
  publicId: string,
): boolean {
  return Boolean(publicId) && publicId.startsWith(mentionIdPrefix(type));
}

/**
 * Strips characters that break assistant-ui directive syntax {@code :type[label]{name=id}}.
 * Empty results fall back to a stable placeholder so serialize/parse stay round-trippable.
 */
export function sanitizeMentionLabel(label: string): string {
  const cleaned = label
    .replace(/[\r\n\u2028\u2029]+/g, " ")
    .replace(/[\[\]]/g, "")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "Untitled";
}
